import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SHOW_PENDING } from "@/content/site";

// Marcadores {{...}} viram pendências visíveis só na pré-visualização.
export const Rich = ({ text }) => {
    const parts = String(text).split(/(\{\{[^}]+\}\})/g);
    return parts.map((p, i) =>
        p.startsWith("{{") ? <Pendente key={i}>{p.slice(2, -2)}</Pendente> : <React.Fragment key={i}>{p}</React.Fragment>
    );
};

export const Pendente = ({ children }) => !SHOW_PENDING ? null : (
    <span
        data-testid="pending-placeholder"
        className="inline-block align-baseline font-sans not-italic text-[11px] tracking-[0.04em] text-[#C7A659] border border-dashed border-[#C7A659]/60 rounded-sm px-1.5 py-0.5 bg-[#C7A659]/5"
    >
        [{children}]
    </span>
);

export const Eyebrow = ({ children, light = false, className = "" }) => (
    <p className={`flex items-center gap-3 text-[10.5px] uppercase tracking-[0.32em] font-sans font-normal mb-5 ${light ? "text-[#6b6a52]" : "text-[#C7A659]"} ${className}`}>
        <span aria-hidden="true" className={`block w-6 h-px ${light ? "bg-[#6b6a52]" : "bg-[#C7A659]"}`} />
        {children}
    </p>
);

export const H2 = ({ children, light = false, className = "" }) => (
    <h2 className={`font-serif font-light text-[30px] sm:text-[38px] lg:text-[46px] leading-[1.1] tracking-[-0.01em] text-balance ${light ? "text-[#0E2018]" : "text-[#F2EFE5]"} ${className}`}>
        {children}
    </h2>
);

export const Body = ({ children, light = false, className = "" }) => (
    <p className={`font-sans font-light text-[14.5px] md:text-[16px] leading-[1.8] ${light ? "text-[#2d3a31]" : "text-[#B0B6A6]"} ${className}`}>
        {children}
    </p>
);

export const Container = ({ children, className = "" }) => (
    <div className={`max-w-6xl mx-auto px-6 md:px-10 ${className}`}>{children}</div>
);

export const ArrowLink = ({ to, href, children, testId, light = false }) => {
    const cls = `group inline-flex items-center gap-3 font-sans text-[11.5px] uppercase tracking-[0.24em] transition-colors ${light ? "text-[#0E2018] hover:text-[#8a6a22]" : "text-[#C7A659] hover:text-[#e2c886]"}`;
    const inner = (
        <>
            <span className="border-b border-current pb-1">{children}</span>
            <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-1">→</span>
        </>
    );
    if (href) {
        return (
            <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId} className={cls}>
                {inner}
            </a>
        );
    }
    return (
        <Link to={to} data-testid={testId} className={cls}>
            {inner}
        </Link>
    );
};

// Revela o conteúdo ao entrar na tela (respeita prefers-reduced-motion via CSS).
export const Reveal = ({ children, delay = 0, className = "" }) => {
    const ref = useRef(null);
    const [shown, setShown] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([e]) => {
                if (e.isIntersecting) {
                    setShown(true);
                    obs.disconnect();
                }
            },
            { threshold: 0.12 }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);
    return (
        <div ref={ref} className={`reveal ${shown ? "reveal--in" : ""} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
            {children}
        </div>
    );
};

// Faixa fotográfica com véu verde — imagem real, nunca protagonista do texto.
export const PhotoBand = ({ src, alt, className = "", veil = 0.55, position = "center" }) => (
    <div className={`overflow-hidden ${className || "relative"}`}>
        <img src={src} alt={alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: position, filter: "saturate(0.85) contrast(1.05)" }} />
        <div aria-hidden="true" className="absolute inset-0" style={{ background: `rgba(14,32,24,${veil})` }} />
    </div>
);

// Classificação epistêmica visível: evita misturar evidência, hipótese e lentes.
export const EpistemicTag = ({ tipo, light = false }) => {
    const label = { evidencia: "Evidência empírica/factual", interpretacao: "Interpretação científica", hipotese: "Hipótese em investigação", "lente-teorica": "Lente teórica", "lente-filosofica": "Lente filosófica", questao: "Questão de investigação", tese: "Tese em construção", "questao-aberta": "Questão em investigação", nucleo: "Núcleo doutoral", transversal: "Dimensão transversal", representacao: "Representação conceitual em desenvolvimento" }[tipo];
    if (!label) return null;
    return (
        <span data-testid={`epistemic-${tipo}`} className={`inline-flex items-center gap-1.5 font-sans text-[9.5px] uppercase tracking-[0.2em] rounded-full px-2.5 py-1 border ${light ? "border-[#0E2018]/25 text-[#4a5a4f]" : "border-[#C7A659]/35 text-[#C7A659]/90"}`}>
            <span aria-hidden="true" className="w-1 h-1 rounded-full bg-current" />
            {label}
        </span>
    );
};
